import React, { useState, useRef, useEffect } from 'react';
import { MERGE_TAGS } from '../utils/mergeTagHelper';

interface MentionAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  className?: string;
  isDarkMode: boolean;
  rows?: number;
  multiline?: boolean;
}

export const MentionAutocomplete: React.FC<MentionAutocompleteProps> = ({
  value,
  onChange,
  onBlur,
  placeholder,
  className = '',
  isDarkMode,
  rows = 4,
  multiline = false
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState(MERGE_TAGS);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mentionStart, setMentionStart] = useState<number | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  
  const currentRef = multiline ? textareaRef : inputRef;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const textarea = textareaRef.current;
      const input = inputRef.current;
      const currentElement = multiline ? textarea : input;
      
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        currentElement &&
        !currentElement.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [multiline]);

  const getCaretPosition = (): number => {
    const element = currentRef.current;
    if (!element) return 0;
    return element.selectionStart || 0;
  };

  const setCaretPosition = (position: number) => {
    const element = currentRef.current;
    if (!element) return;
    element.setSelectionRange(position, position);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart || 0;
    
    onChange(newValue);

    // Check if we're typing after an @
    const textBeforeCursor = newValue.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      // Check if there's a space or newline after @ (meaning mention is complete)
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      const hasSpaceOrNewline = /\s/.test(textAfterAt);
      
      if (!hasSpaceOrNewline) {
        // We're in a mention - show suggestions
        const query = textAfterAt.toLowerCase();
        const filtered = MERGE_TAGS.filter(tag =>
          tag.name.toLowerCase().includes(query) ||
          tag.value.toLowerCase().includes(query)
        );
        setSuggestions(filtered);
        setMentionStart(lastAtIndex);
        setShowSuggestions(true);
        setSelectedIndex(0);
        return;
      }
    }
    
    setShowSuggestions(false);
    setMentionStart(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if (!showSuggestions) {
      if (onBlur && e.key === 'Enter' && !multiline) {
        onBlur();
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      insertMention(suggestions[selectedIndex]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setMentionStart(null);
    }
  };

  const insertMention = (tag: typeof MERGE_TAGS[0]) => {
    if (!currentRef.current || mentionStart === null) return;

    const element = currentRef.current;
    const cursorPos = element.selectionStart || 0;
    const textBefore = value.substring(0, mentionStart);
    const textAfter = value.substring(cursorPos);
    
    const newValue = textBefore + tag.value + textAfter;
    onChange(newValue);
    
    const newCursorPos = mentionStart + tag.value.length;
    setTimeout(() => {
      setCaretPosition(newCursorPos);
      element.focus();
    }, 0);
    
    setShowSuggestions(false);
    setMentionStart(null);
  };

  const inputClassName = `
    w-full px-3 py-2 border rounded-md resize-none
    ${isDarkMode 
      ? 'bg-gray-700 border-gray-600 text-white' 
      : 'bg-white border-gray-300 text-gray-900'
    }
    ${className}
  `;

  const suggestionsClassName = `
    absolute z-50 mt-1 max-h-60 overflow-auto rounded-md shadow-lg border
    ${isDarkMode 
      ? 'bg-gray-800 border-gray-600' 
      : 'bg-white border-gray-300'
    }
  `;

  const suggestionItemClassName = (index: number) => `
    px-4 py-2 cursor-pointer flex items-center justify-between
    ${index === selectedIndex
      ? isDarkMode
        ? 'bg-accent-600 text-white'
        : 'bg-accent-50 text-accent-900'
      : isDarkMode
        ? 'text-gray-300 hover:bg-gray-700'
        : 'text-gray-900 hover:bg-gray-50'
    }
  `;

  return (
    <div className="relative">
      {multiline ? (
        <textarea
          ref={textareaRef}
          rows={rows}
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={onBlur}
          placeholder={placeholder}
          className={inputClassName}
        />
      ) : (
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={onBlur}
          placeholder={placeholder}
          className={inputClassName}
        />
      )}
      
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className={suggestionsClassName}
          style={{
            top: '100%',
            left: 0,
            right: 0,
            marginTop: '4px'
          }}
        >
          {suggestions.map((tag, index) => (
            <div
              key={tag.value}
              className={suggestionItemClassName(index)}
              onClick={() => insertMention(tag)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <div>
                <div className={`font-medium ${index === selectedIndex ? 'text-white' : isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                  {tag.name}
                </div>
                <div className={`text-xs ${index === selectedIndex ? 'text-accent-100' : isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {tag.value}
                </div>
              </div>
              <span className={`text-xs px-2 py-1 rounded ${index === selectedIndex ? 'bg-accent-500 text-white' : isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                Sample: {tag.sample}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
